/**
 * SmartPickupResult — recommended-pickup result.
 *
 * "RECOMMENDED PICKUP" screen with the "Why this pickup time?" breakdown.
 * Two modes, distinguished by whether a flight was looked up:
 *   • flight mode  (SMART_TIME_FLIGHT_DETAILS present) → renders the flight
 *     header card at the top (Image #5), flight time read from the flight.
 *   • time mode    (STORAGE_KEYS.SMART_PICKUP_FLIGHT_TIME present)     → no header (Image #4),
 *     flight time is the value the user typed.
 *
 * Recommendation math reuses the production formula (toAirport: flight −
 * drive − buffer; fromAirport: flight + buffer). Both the buffer AND the
 * "Why this pickup time?" rows come from the airport's own waiting_times bucket
 * on /config/airports — different airports have different waiting times, so
 * nothing here is hardcoded per flight type any more.
 *
 * "Use Recommended Pickup" commits RIDE_TIME + SMART_TIME_SELECTED and advances
 * to selectedCarDetails (or orderDetails in edit mode). "Set pickup time manually"
 * returns to the wheel.
 */
import React, {useState, useEffect} from "react";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import {ModalFooter} from "../Components/BookingModalShell.jsx";
import {flightService} from "@/services/flightService";
import {isNearAirport, getWaitingBucket, getAirportBufferMinutes, getWaitingItemIcon, getArriveEarlyMinutes, isMissingRequiredFlightNumber} from "@/utils/airports";
import {storage, STORAGE_KEYS} from "@/utils/storage";
import {LiabilityDisclaimerContent} from "./liabilityDisclaimer.jsx";
import {FindingDriver} from "./findingDriver.jsx";
import {img} from "@/utils/pluginImg.js";
import {airlineLogo} from "@/utils/airlineLogo.js";

const roundUp5 = (date) => {
    const d = new Date(date);
    const m = d.getMinutes();
    const r = m % 5;
    if (r !== 0) d.setMinutes(m + (5 - r), 0, 0);
    else d.setSeconds(0, 0);
    return d;
};

const formatTime = (date) =>
    date ? date.toLocaleTimeString("en-US", {hour: "numeric", minute: "2-digit"}) : "";
const formatDateLabel = (date) =>
    date ? date.toLocaleDateString("en-US", {weekday: "short", month: "short", day: "numeric"}) : "";

// "Toronto Pearson International Airport" → "Toronto Pearson"
const cleanAirportName = (name) =>
    (name || "").replace(/\s*International Airport$/i, "").replace(/\s*Airport$/i, "").trim();

const formatDurationHM = (minutes) => {
    const total = Math.max(0, Math.round(Number(minutes) || 0));
    const h = Math.floor(total / 60);
    const m = total % 60;
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h} hr`;
    return `${h} hr ${m} min`;
};

// Resolve a movement's effective time: prefer the airline's revised time, then
// AeroDataBox's predicted, else scheduled — and report whether the time/date
// changed vs the schedule (so a delayed flight shifts the pickup AND we can show
// the new time with the old one struck through, mirroring the old flow).
const resolveMovement = (mv) => {
    const scheduled = mv?.scheduledTime?.local || "";
    const revised = mv?.revisedTime?.local || "";
    const predicted = mv?.predictedTime?.local || "";
    let effective = scheduled;
    if (revised && revised !== scheduled) effective = revised;
    else if (predicted && predicted !== scheduled) effective = predicted;
    const timeChanged = !!effective && !!scheduled && effective !== scheduled;
    const dateChanged = timeChanged
        && flightService.formatFlightDate(effective) !== flightService.formatFlightDate(scheduled);
    // Signed shift vs schedule: > 0 = later (delayed), < 0 = earlier (early).
    let deltaMs = 0;
    if (timeChanged) {
        const es = flightService.parseLocalTime(effective)?.getTime();
        const sc = flightService.parseLocalTime(scheduled)?.getTime();
        if (Number.isFinite(es) && Number.isFinite(sc)) deltaMs = es - sc;
    }
    return {scheduled, effective, timeChanged, dateChanged, deltaMs};
};

export function SmartPickupResult(props) {

    // Every read below prefers a PROP and falls back to session storage — see the note
    // in smartPickupRecommendation. Booking passes nothing; edit/confirm hand in the
    // trip's details and take the chosen time through onCommit.
    const flightDetails = props.flightDetails !== undefined
        ? props.flightDetails
        : storage.get(STORAGE_KEYS.SMART_TIME_FLIGHT_DETAILS, null);
    const enteredTimeStr = props.enteredTime !== undefined
        ? props.enteredTime
        : storage.get(STORAGE_KEYS.SMART_PICKUP_FLIGHT_TIME, null);
    const isFlightMode = !!flightDetails;

    // Direction: destinations = [pickup, dropoff, ...]; dropoff is index 1.
    const destinations = props.destinations ?? storage.get(STORAGE_KEYS.DESTINATIONS, []);
    const firstDest = destinations[0];
    const lastDest = destinations[1];
    const firstAirport = firstDest?.lat && firstDest?.lng ? isNearAirport(firstDest.lat, firstDest.lng) : null;
    const lastAirport = lastDest?.lat && lastDest?.lng ? isNearAirport(lastDest.lat, lastDest.lng) : null;
    const isFromAirport = !!firstAirport;

    // Flight type. A looked-up flight is classified from its OWN airports — the
    // flight-number popup has no International/Domestic toggle, so the stored value is
    // only meaningful on the typed-time path and otherwise falls back to "International"
    // (which labelled domestic hops like YYZ→YUL as international).
    const flightType = (isFlightMode && flightService.getFlightType(flightDetails))
        || props.flightType
        || storage.get(STORAGE_KEYS.SMART_PICKUP_FLIGHT_TYPE)
        || "International";
    const isIntl = flightType === "International";

    // "Total estimated time at airport" info icon → Liability Disclaimer, dual-mode:
    // mobile pushes the overlay step (POPUP_STEPS), desktop opens it inline. Same
    // behaviour and same content as the old smartTimeSelect flow.
    const [liabilityPopup, setLiabilityPopup] = useState(false);
    const [isMobile, setIsMobile] = useState(
        typeof window !== "undefined" && window.innerWidth <= 575
    );
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= 575);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);
    const openLiability = () => {
        // Outside the booking modal there is no overlay step to push, so the caller
        // supplies its own sheet (see SmartPickupFlow).
        if (props.onOpenLiability) props.onOpenLiability();
        else if (isMobile) props.setStep("liabilityDisclaimer");
        else setLiabilityPopup(true);
    };

    // "How to find my driver" (fromAirport only) → the same arrivals guide the
    // bookingReceived step and the perks FAQ open. Dual-mode like the disclaimer above:
    // mobile pushes the findingDriver overlay step (it is in POPUP_STEPS), desktop opens
    // it inline.
    const [findingDriverPopup, setFindingDriverPopup] = useState(false);
    const openFindingDriver = () => {
        if (props.onOpenFindingDriver) props.onOpenFindingDriver();
        else if (isMobile) props.setStep("findingDriver");
        else setFindingDriverPopup(true);
    };

    // THE RECOMMENDATION AND THE BREAKDOWN BOTH COME FROM THIS AIRPORT'S OWN
    // waiting_times bucket (/config/airports), picked by direction × flight scope:
    // fromAirport+International → from_airport_international, and so on. An
    // unconfigured bucket falls back to the global booking.{from,to}_airport_extra_minutes
    // — see utils/airports.js. Read HERE, inside the component: both are getters over
    // the react-query cache and are empty at module scope.
    //
    // Because the rows and the cushion are now the SAME data, the total shown below can
    // finally be summed from the rows without contradicting the recommendation — which
    // is what the old hardcoded rows did (International from-airport read 90 + 45 = 135
    // while the pickup was computed from a backend 80).
    const legAirport = isFromAirport ? firstAirport : lastAirport;
    const waitingOpts = {fromAirport: isFromAirport, international: isIntl};
    const waitingBucket = getWaitingBucket(legAirport, waitingOpts);
    const buffer = getAirportBufferMinutes(legAirport, waitingOpts);

    const routeBreakdown = props.routeBreakdown ?? storage.get(STORAGE_KEYS.ROUTE_BREAKDOWN, null);
    const driveMinutes = routeBreakdown?.duration_minutes || 45;

    // The flight time we anchor on: departure for toAirport, arrival for fromAirport.
    const anchorMv = isFlightMode ? (isFromAirport ? flightDetails.arrival : flightDetails.departure) : null;
    const mvResolved = anchorMv ? resolveMovement(anchorMv) : null;
    let flightTime = null;
    if (mvResolved) {
        flightTime = mvResolved.effective ? flightService.parseLocalTime(mvResolved.effective) : null;
    } else if (enteredTimeStr) {
        flightTime = new Date(enteredTimeStr);
    }

    // Guard: no anchor time → send the user back to choose again.
    if (!flightTime || Number.isNaN(flightTime.getTime())) {
        return (
            <>
                <ModalHeader
                    title="Recommended pickup"
                    totalSteps={0}
                    cancelButton={!!props.showClose}
                    cancelButtonType="icon"
                    ignoreEditMode={true}
                    backStep={"smartPickupRecommendation"}
                    showBackButton={!props.hideBack}
                    setStep={props.setStep}
                    closeModal={props.closeModal}
                />
                <div className="modalPages-spacing padding-bottom-30">
                    <div className="hopn-quick-booking-error">
                        We couldn't work out a pickup time. Please choose your flight time again.
                    </div>
                </div>
            </>
        );
    }

    // Recommendation + "arrive at airport" (toAirport) / pickup (fromAirport).
    let recommended;
    let arriveAtAirport = null;
    if (isFromAirport) {
        recommended = roundUp5(new Date(flightTime.getTime() + buffer * 60000));
    } else {
        recommended = roundUp5(new Date(flightTime.getTime() - (driveMinutes + buffer) * 60000));
        arriveAtAirport = new Date(flightTime.getTime() - buffer * 60000);
    }

    // Airport label for the subtitle.
    const airportObj = isFromAirport ? firstAirport : lastAirport;
    let airportLabel = cleanAirportName(airportObj?.name);
    if (!airportLabel && isFlightMode) {
        const ap = isFromAirport ? flightDetails.arrival?.airport : flightDetails.departure?.airport;
        airportLabel = cleanAirportName(ap?.municipalityName || ap?.name);
    }
    if (!airportLabel) airportLabel = "the airport";

    // Terminal is only knowable from real flight data. A time-only suggestion has
    // no flight behind it, and even a looked-up flight may not report one — in both
    // cases render nothing rather than inventing "Terminal 1".
    const terminal =
        (isFlightMode
            ? (isFromAirport ? flightDetails.arrival?.terminal : flightDetails.departure?.terminal)
            : null) || null;

    const flightTimeLabel = formatTime(flightTime);
    const confirmDateTimeStr = `${formatTime(recommended)}, ${formatDateLabel(recommended)}`;

    // null unless we ship artwork for this carrier — see utils/airlineLogo.js.
    const airlineLogoSrc = airlineLogo(flightDetails?.airline?.name);
    // Automated status: Cancelled → Delayed (anchor movement now LATER, or the
    // provider says so) → Early (anchor movement now EARLIER) → On time. A time
    // change alone isn't "delayed" — the direction of the change decides.
    const statusStr = (flightDetails?.status || "").toString().toLowerCase();
    const isCancelledFlight = statusStr.startsWith("cancel");
    const isDelayed = !isCancelledFlight && ((mvResolved && mvResolved.deltaMs > 0) || statusStr.includes("delay"));
    const isEarly = !isCancelledFlight && !isDelayed && !!mvResolved && mvResolved.deltaMs < 0;
    const statusLabel = isCancelledFlight ? "Cancelled" : (isDelayed ? "Delayed" : (isEarly ? "Early" : "On time"));

    // Editing an existing booking inside the BOOKING MODAL (the flight card's pencil on
    // orderDetails). Deliberately NOT `props.onManual`: on /edit and /confirm that
    // callback closes the flow back to the page's own date/time picker, which is the
    // only way out of the sheet — hiding the link there would strand the passenger.
    const isEditMode = storage.get(STORAGE_KEYS.EDIT_STEP) === true && !props.onManual;

    const goManual = () => {
        if (props.onManual) { props.onManual(); return; }
        storage.set(STORAGE_KEYS.SMART_PICKUP_SCROLL_PICKER, "1");
        props.setStep("travelingDateTime");
    };

    const handleUse = () => {
        if (props.onCommit) { props.onCommit(recommended); return; }

        // Booking flow only (onCommit above means we're inside the edit/confirm wrapper,
        // which checks the trip resource instead of the flow's storage). A from-airport
        // recommendation can be reached in TIME mode, and the chooser's ✕ can take an
        // applied flight back off — either way the booking would leave here with no
        // flight number, which the API now refuses. Back to the chooser, which is where
        // the number is entered; nothing is committed, so the same recommendation stands.
        if (isMissingRequiredFlightNumber()) {
            props.setStep("smartPickupRecommendation");
            return;
        }

        storage.set(STORAGE_KEYS.RIDE_TIME, recommended.toString());
        storage.set(
            STORAGE_KEYS.RIDE_DATE,
            `${recommended.getFullYear()}-${String(recommended.getMonth() + 1).padStart(2, "0")}-${String(recommended.getDate()).padStart(2, "0")}`
        );
        storage.set(STORAGE_KEYS.SMART_TIME_SELECTED, "1");
        if (storage.get(STORAGE_KEYS.EDIT_STEP) === true) {
            props.setStep("orderDetails");
        } else {
            props.setStep("selectedCarDetails");
        }
    };

    // Breakdown rows. The airport's waiting_times items ARE the list — labels and
    // minutes both come from the backend, so a different airport shows a different
    // breakdown without a redeploy.
    const flightNote = `${flightType} flight`;
    const waitingRows = (waitingBucket?.items ?? []).map((item) => {
        // Traffic reads as an ADDITION to the drive, so it keeps the design's red
        // "+20 min" treatment; everything else is an approximate allowance.
        const isTraffic = /traffic/i.test(item.name);
        return {
            icon: getWaitingItemIcon(item.name),
            label: item.name,
            value: `${isTraffic ? "+" : "~"}${formatDurationHM(item.minutes)}`,
            negative: isTraffic,
        };
    });

    // fromAirport pickup is set by how long it takes to EXIT the airport, so the
    // backend items are the whole story (drive/traffic happen AFTER pickup and don't
    // affect it) — shown exactly as they arrive.
    //
    // toAirport keeps OUR two rows in their designed positions — flight departure
    // first, drive time third — and the backend items fill in around them (the first
    // one, typically the arrival buffer, sits between them; anything after, typically
    // predicted traffic, follows the drive).
    const breakdownRows = isFromAirport
        ? waitingRows
        : [
            {icon: "plane-take-off.svg", label: "Flight departure", note: flightNote, value: flightTimeLabel},
            ...waitingRows.slice(0, 1),
            {icon: "bold-car.svg", label: "Drive time", note: "Current traffic conditions", value: `~${formatDurationHM(driveMinutes)}`},
            ...waitingRows.slice(1),
        ];

    // "Total estimated time at airport". fromAirport is the cushion as-is (how long
    // getting out of the airport takes); toAirport adds OUR drive leg on top of it,
    // because that is the row the backend can't know about. Both use the SAME `buffer`
    // the recommendation is computed from, so the total can no longer drift from it.
    const totalLabel = `~${formatDurationHM(isFromAirport ? buffer : buffer + driveMinutes)}`;

    // How early the passenger actually stands in the terminal, for the note above the
    // rows: the cushion MINUS its road allowance (a "predicted traffic" item pads the
    // journey, not the check-in time).
    const arriveEarlyMinutes = getArriveEarlyMinutes(buffer, waitingBucket?.items);

    return (
        <>
            {/* Liability Disclaimer info popup (desktop). Mobile pushes the
                liabilityDisclaimer overlay step instead — see openLiability. */}
            <div
                className="upper-modal liabilityDisclaimer"
                style={{display: liabilityPopup ? "flex" : "none"}}
            >
                <div className="upper-modal-inner">
                    <ModalHeader
                        inline={true}
                        title=""
                        extraClasses=""
                        totalSteps={0}
                        cancelButton={true}
                        cancelButtonType="icon"
                        backStep={null}
                        showBackButton={false}
                        setStep={props.setStep}
                        closeModal={() => setLiabilityPopup(false)}
                    />
                    <div className="upper-modal-content padding-bottom-30">
                        <div className="hopn-policies-disclaimer modalPages-spacing">
                            <LiabilityDisclaimerContent/>
                        </div>
                    </div>
                </div>
            </div>

            {/* "How to find my driver" guide popup (desktop). Mobile pushes the
                findingDriver overlay step instead — see openFindingDriver. */}
            <div
                className="upper-modal findingDriver"
                style={{display: findingDriverPopup ? "flex" : "none"}}
            >
                <div className="upper-modal-inner">
                    <ModalHeader
                        inline={true}
                        title=""
                        extraClasses="less-space"
                        totalSteps={0}
                        cancelButton={true}
                        cancelButtonType="icon"
                        backStep={null}
                        showBackButton={false}
                        setStep={props.setStep}
                        closeModal={() => setFindingDriverPopup(false)}
                    />
                    {/* The dashboard's FindingDriver renders FLUSH (the plugin's carries its
                        own gutters), so modalPages-spacing has to supply them here — same
                        wrapper the FAQ's copy of this popup uses. */}
                    <div className="upper-modal-content padding-bottom-30">
                        <div className="modalPages-spacing">
                            <FindingDriver
                                hideHeader={true}
                                setStep={props.setStep}
                                closeModal={() => setFindingDriverPopup(false)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* `hideBack`: no way back to the chooser. The confirm page opens straight
                here off the trip's own flight, so back would offer to re-pick a flight
                the booking is already committed to. */}
            <ModalHeader
                title="Recommended pickup"
                extraClasses=""
                totalSteps={0}
                cancelButton={!!props.showClose}
                cancelButtonType="icon"
                ignoreEditMode={true}
                backStep={"smartPickupRecommendation"}
                showBackButton={!props.hideBack}
                setStep={props.setStep}
                closeModal={props.closeModal}
            />

            {/* is-time / is-flight let the stylesheet target one mode — the two share
                every block below except the flight header, so there was no hook for
                mode-specific spacing until now. */}
            <div className={`modalPages-spacing hopn-rp ${isFlightMode ? 'is-flight' : 'is-time'}`}>
                {/* Flight header card — flight mode only (Image #5). */}
                {isFlightMode && (
                    <div className="hopn-rp-flight-header">
                        <div className="hopn-rp-fh-top">
                            <div className="hopn-rp-fh-airline">
                                {airlineLogoSrc && (
                                    <span className="hopn-rp-fh-logo">
                                        <img src={airlineLogoSrc} alt=""/>
                                    </span>
                                )}
                                <div className="hopn-rp-fh-airline-text">
                                    {flightDetails.airline?.name && flightDetails.number && (
                                        <span className="hopn-rp-fh-airline-name">{flightDetails.airline.name}</span>
                                    )}
                                    <strong>{flightDetails.number || flightDetails.airline?.name}</strong>
                                    <span className={`hopn-rp-fh-status ${statusLabel === "Cancelled" ? "is-cancelled" : statusLabel === "Delayed" ? "is-delayed" : ""}`}>
                                        {statusLabel}
                                        {statusLabel === "On time" && (
                                            <svg className="hopn-rp-fh-status-check" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                <circle cx="8" cy="8" r="8" fill="#1f9d6a"/>
                                                <path d="M4.6 8.2l2.1 2.1 4.7-4.7" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        )}
                                    </span>
                                </div>
                            </div>
                            {/* Column order is direction-aware:
                                  toAirport   → [Departure] [From] [To]  (date/time on the LEFT)
                                  fromAirport → [From] [To] [Arrival]     (date/time on the RIGHT)
                                The time column is anchored on the same movement the
                                recommendation is computed from. */}
                            {(() => {
                                const fromCol = (
                                    <div className="hopn-rp-fh-col" key="from">
                                        <small>From</small>
                                        <b>{flightDetails.departure?.airport?.iata || "—"}</b>
                                        <span>{cleanAirportName(flightDetails.departure?.airport?.municipalityName || flightDetails.departure?.airport?.name)}</span>
                                    </div>
                                );
                                const toCol = (
                                    <div className="hopn-rp-fh-col" key="to">
                                        <small>To</small>
                                        <b>{flightDetails.arrival?.airport?.iata || "—"}</b>
                                        <span>{cleanAirportName(flightDetails.arrival?.airport?.municipalityName || flightDetails.arrival?.airport?.name)}</span>
                                    </div>
                                );
                                const timeCol = (
                                    <div className="hopn-rp-fh-col" key="time">
                                        <small>{isFromAirport ? "Arrival" : "Departure"}</small>
                                        <b>
                                            {mvResolved?.dateChanged ? (
                                                <>
                                                    <span className="hopn-rp-fh-new">{flightService.formatFlightDate(mvResolved.effective)}</span>
                                                    <span className="hopn-rp-fh-old">{flightService.formatFlightDate(mvResolved.scheduled)}</span>
                                                </>
                                            ) : formatDateLabel(flightTime)}
                                        </b>
                                        <b>
                                            {mvResolved?.timeChanged ? (
                                                <>
                                                    <span className="hopn-rp-fh-new">{flightService.formatFlightTime(mvResolved.effective)}</span>
                                                    <span className="hopn-rp-fh-old">{flightService.formatFlightTime(mvResolved.scheduled)}</span>
                                                </>
                                            ) : formatTime(flightTime)}
                                        </b>
                                    </div>
                                );
                                return isFromAirport ? [fromCol, toCol, timeCol] : [timeCol, fromCol, toCol];
                            })()}
                        </div>
                        <div className="hopn-rp-fh-foot">
                            <span className="hopn-rp-fh-type">
                                <svg className="hopn-rp-globe" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <circle cx="12" cy="12" r="9" stroke="#1f9d6a" strokeWidth="1.6"/>
                                    <path d="M3 12h18" stroke="#1f9d6a" strokeWidth="1.6"/>
                                    <path d="M12 3c3 2.4 3 15.6 0 18" stroke="#1f9d6a" strokeWidth="1.6"/>
                                    <path d="M12 3c-3 2.4 -3 15.6 0 18" stroke="#1f9d6a" strokeWidth="1.6"/>
                                </svg>
                                {flightType} flight
                            </span>
                            {terminal && <span>Terminal {terminal}</span>}
                        </div>
                    </div>
                )}

                {/* Hero */}
                <div className="hopn-rp-hero">
                    <span className="hopn-rp-label">RECOMMENDED PICKUP</span>
                    <h2 className="hopn-rp-time">{formatTime(recommended)}</h2>
                    <p className="hopn-rp-sub">
                        {isFromAirport ? (
                            <>We'll be there when you land at <strong>{airportLabel}</strong> for your <strong>{flightTimeLabel}</strong> arrival.</>
                        ) : (
                            <>To reach <strong>{airportLabel}</strong> comfortably for your <strong>{flightTimeLabel}</strong> flight.</>
                        )}
                    </p>
                </div>

                {/* Arrival estimate — flight lookups only. A time-only suggestion has
                    no flight behind it, so we can't say where or when the passenger
                    lands; the breakdown below already explains the pickup time. */}
                {isFlightMode && (
                    <div className="hopn-rp-arrival">
                        <span className="hopn-rp-arrival-icon">
                            <img src={img("bold-car.svg")} alt=""/>
                        </span>
                        <div className="hopn-rp-arrival-text">
                            <small>{isFromAirport ? "We'll pick you up around" : "You'll arrive around"}</small>
                            <strong>{formatTime(isFromAirport ? recommended : arriveAtAirport)}</strong>
                        </div>
                        {terminal && <span className="hopn-rp-arrival-terminal">Terminal {terminal}</span>}
                    </div>
                )}

                {/* Why this pickup time */}
                <div className="hopn-rp-why">
                    <h4>Why this pickup time?</h4>
                    {/* One note either way — what reassures the passenger differs by direction:
                        leaving the airport, the pickup follows the plane; heading to it, the
                        buffer built into the breakdown below is what needs explaining. */}
                    <p className="hopn-rp-why-note">
                        {isFromAirport
                            ? "Your driver monitors your flight and adjusts pickup time automatically."
                            /* The cushion is per airport now, so the figure is read from it
                               rather than the old fixed "2-3 hours". */
                            : `We recommend arriving at the airport about ${formatDurationHM(arriveEarlyMinutes)} before your flight departure.`}
                    </p>
                    <ul className="hopn-rp-breakdown">
                        {breakdownRows.map((row, i) => (
                            <li key={i}>
                                <span className="hopn-rp-bd-icon">
                                    <img src={img(row.icon)} alt=""/>
                                </span>
                                {/* Backend rows carry a label only — no invented sub-copy
                                    for them; the row centres itself without the <small>. */}
                                <div className="hopn-rp-bd-label">
                                    <b>{row.label}</b>
                                    {row.note && <small>{row.note}</small>}
                                </div>
                                <span className={`hopn-rp-bd-value ${row.negative ? "is-negative" : ""}`}>{row.value}</span>
                            </li>
                        ))}
                    </ul>
                    {/* Both directions carry the total + its Liability Disclaimer info
                        icon; only the figure and its qualifier differ. */}
                    <div className="hopn-rp-total">
                        <span><strong>Total</strong> estimated time at airport:</span>
                        <strong className="hopn-rp-total-value">
                            {isFromAirport ? `${totalLabel} after landing` : `${totalLabel} before departure`}
                            <button type="button" className="hopn-info-icon" onClick={openLiability}>
                                <img src={img("info-icon.svg")} alt="Liability disclaimer"/>
                            </button>
                        </strong>
                    </div>
                </div>

                {/* Monitoring note */}
                <div className="hopn-rp-monitor">
                    <span className="hopn-rp-monitor-icon">
                        <img src={img("success-shild.svg")} alt=""/>
                    </span>
                    <p>
                        We monitor your flight and traffic in real time and will adjust if anything changes.
                        {isFromAirport && (
                            <button type="button" className="hopn-rp-monitor-link" onClick={openFindingDriver}>
                                How to find my driver
                            </button>
                        )}
                    </p>
                </div>

                <ModalFooter>
                    <div className="hopn-rp-actions">
                        <button
                            type="button"
                            className="hopn-add-passenger-button duble-title hopn-rp-confirm"
                            onClick={handleUse}
                        >
                            <div className="multiple-text">
                                <span>Use Recommended Pickup</span>
                                <span>{confirmDateTimeStr}</span>
                            </div>
                        </button>
                        {/* Editing an existing booking: the pickup time already has its
                            own edit pencil on orderDetails, so this link would be a second
                            route to the wheel from inside a flight edit. Hidden there —
                            "Use Recommended Pickup" is the only way out. */}
                        {!isEditMode && (
                            <button type="button" className="hopn-choose-own-time-link" onClick={goManual}>
                                Set pickup time manually
                                <img src={img("arr-right.svg")} alt=""/>
                            </button>
                        )}
                    </div>
                </ModalFooter>
            </div>
        </>
    );
}

export default SmartPickupResult;
